import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { 
  Video, Plus, Play, Pause, SkipForward, Trash2, 
  Eye, MousePointerClick, Clock, Settings, Upload,
  Check, X, ExternalLink, Radio, Image
} from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";
import { API, getAuthHeader } from "./utils";

export const VideoAdsTab = () => {
  const [ads, setAds] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBillboardModal, setShowBillboardModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [channels, setChannels] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const billboardInputRef = useRef(null);

  const fetchData = async () => {
    try {
      const [adsRes, statsRes, channelsRes] = await Promise.all([
        axios.get(`${API}/video-ads`, { headers: getAuthHeader() }),
        axios.get(`${API}/video-ads/stats`, { headers: getAuthHeader() }),
        axios.get(`${API}/channels`, { headers: getAuthHeader() })
      ]);
      setAds(adsRes.data);
      setStats(statsRes.data);
      setChannels(channelsRes.data || []);
    } catch (error) {
      console.error("Error fetching video ads:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", file.name.replace(/\.[^/.]+$/, ""));
    formData.append("ad_type", "pre_roll");
    formData.append("duration_seconds", "15");
    formData.append("is_skippable", "true");
    formData.append("skip_after_seconds", "5");

    try {
      const response = await axios.post(`${API}/video-ads/upload`, formData, {
        headers: {
          ...getAuthHeader(),
          "Content-Type": "multipart/form-data"
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });

      setAds([response.data.ad, ...ads]);
      setEditingAd(response.data.ad);
      fetchData();
    } catch (error) {
      console.error("Error uploading video ad:", error);
      alert("Failed to upload video. Please try again.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleBillboardUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", file.name.replace(/\.[^/.]+$/, ""));
    formData.append("ad_type", "pre_roll");
    formData.append("ad_format", "billboard");
    formData.append("duration_seconds", "10");
    formData.append("is_skippable", "true");
    formData.append("skip_after_seconds", "3");

    try {
      const response = await axios.post(`${API}/video-ads/upload-billboard`, formData, {
        headers: {
          ...getAuthHeader(),
          "Content-Type": "multipart/form-data"
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });

      setAds([response.data.ad, ...ads]);
      setEditingAd(response.data.ad);
      fetchData();
    } catch (error) {
      console.error("Error uploading billboard:", error);
      alert("Failed to upload billboard image. Please try again.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (billboardInputRef.current) billboardInputRef.current.value = "";
    }
  };

  const toggleAdActive = async (adId) => {
    try {
      const response = await axios.post(`${API}/video-ads/${adId}/toggle`, {}, { headers: getAuthHeader() });
      setAds(ads.map(ad => ad.id === adId ? { ...ad, is_active: response.data.is_active } : ad));
    } catch (error) {
      console.error("Error toggling ad:", error);
    }
  };

  const deleteAd = async (adId) => {
    if (!window.confirm("Are you sure you want to delete this video ad?")) return;
    
    try {
      await axios.delete(`${API}/video-ads/${adId}`, { headers: getAuthHeader() });
      setAds(ads.filter(ad => ad.id !== adId));
      fetchData(); // Refresh stats
    } catch (error) {
      console.error("Error deleting ad:", error);
    }
  };

  const saveAdChanges = async (adData) => {
    try {
      const response = await axios.put(`${API}/video-ads/${adData.id}`, adData, { headers: getAuthHeader() });
      setAds(ads.map(ad => ad.id === adData.id ? response.data : ad));
      setEditingAd(null);
    } catch (error) {
      console.error("Error updating ad:", error);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin mx-auto"></div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white">
            Pre/Mid Roll Ads
          </h2>
          <p className="text-sm text-zinc-500 mt-1">
            Upload video ads to play before or during streams on all channels
          </p>
        </div>
        <div className="flex gap-2">
          <input
            type="file"
            ref={billboardInputRef}
            accept="image/*"
            onChange={handleBillboardUpload}
            className="hidden"
          />
          <Button 
            onClick={() => billboardInputRef.current?.click()}
            disabled={uploading}
            className="bg-zinc-800 text-white hover:bg-zinc-700 text-xs px-4 py-2 h-auto rounded-none"
            data-testid="upload-billboard-btn"
          >
            <Image className="w-4 h-4 mr-2" />
            {uploading ? `Uploading ${uploadProgress}%` : "Add Billboard"}
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            accept="video/*"
            onChange={handleFileUpload}
            className="hidden"
          />
          <Button 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-white text-black hover:bg-zinc-200 text-xs px-4 py-2 h-auto rounded-none"
            data-testid="upload-video-ad-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? `Uploading ${uploadProgress}%` : "Upload Video Ad"}
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <div className="bg-zinc-900/50 border border-white/5 p-4">
            <p className="text-2xl font-bold text-white">{stats.total_ads}</p>
            <p className="text-xs text-zinc-500 uppercase tracking-widest">Total Ads</p>
          </div>
          <div className="bg-zinc-900/50 border border-white/5 p-4">
            <p className="text-2xl font-bold text-green-500">{stats.active_ads}</p>
            <p className="text-xs text-zinc-500 uppercase tracking-widest">Active</p>
          </div>
          <div className="bg-zinc-900/50 border border-white/5 p-4">
            <p className="text-2xl font-bold text-orange-500">{stats.pre_roll_count}</p>
            <p className="text-xs text-zinc-500 uppercase tracking-widest">Pre-Roll</p>
          </div>
          <div className="bg-zinc-900/50 border border-white/5 p-4">
            <p className="text-2xl font-bold text-blue-500">{stats.mid_roll_count}</p>
            <p className="text-xs text-zinc-500 uppercase tracking-widest">Mid-Roll</p>
          </div>
          <div className="bg-zinc-900/50 border border-white/5 p-4">
            <p className="text-2xl font-bold text-white">{stats.total_views?.toLocaleString() || 0}</p>
            <p className="text-xs text-zinc-500 uppercase tracking-widest">Views</p>
          </div>
          <div className="bg-zinc-900/50 border border-white/5 p-4">
            <p className="text-2xl font-bold text-yellow-500">{stats.completion_rate || 0}%</p>
            <p className="text-xs text-zinc-500 uppercase tracking-widest">Completion</p>
          </div>
          <div className="bg-zinc-900/50 border border-white/5 p-4">
            <p className="text-2xl font-bold text-red-500">{stats.skip_rate || 0}%</p>
            <p className="text-xs text-zinc-500 uppercase tracking-widest">Skip Rate</p>
          </div>
        </div>
      )}

      {/* Ads List */}
      <div>
        <h3 className="font-secondary text-lg font-bold uppercase tracking-tight text-white mb-4">
          Video Ads ({ads.length})
        </h3>
        
        {ads.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900/50 border border-white/5">
            <Video className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">No video ads uploaded yet</p>
            <p className="text-xs text-zinc-600 mt-2">
              Upload a video to get started with pre-roll and mid-roll ads
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {ads.map(ad => (
              <div 
                key={ad.id} 
                className={`bg-zinc-900/50 border ${ad.is_active ? 'border-white/10' : 'border-white/5'} p-4 hover:border-white/20 transition-colors`}
              >
                <div className="flex items-start gap-4">
                  {/* Preview */}
                  <div className="w-48 h-28 bg-black flex-shrink-0 relative overflow-hidden">
                    {ad.ad_format === "billboard" && ad.image_url ? (
                      <img
                        src={ad.image_url.startsWith('/api') ? `${API.replace('/api', '')}${ad.image_url}` : ad.image_url}
                        alt={ad.name}
                        className="w-full h-full object-cover"
                      />
                    ) : ad.video_url ? (
                      <video 
                        src={ad.video_url.startsWith('/api') ? `${API.replace('/api', '')}${ad.video_url}` : ad.video_url}
                        className="w-full h-full object-cover"
                        muted
                        onMouseEnter={(e) => e.target.play()}
                        onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                      />
                    ) : null}
                    <div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/80 text-white text-[10px] font-mono">
                      {ad.duration_seconds}s
                    </div>
                    {ad.ad_format === "billboard" && (
                      <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-purple-500/90 text-white text-[10px] font-bold uppercase">
                        Billboard
                      </div>
                    )}
                    {ad.is_skippable && (
                      <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-yellow-500/90 text-black text-[10px] font-bold uppercase">
                        Skip after {ad.skip_after_seconds}s
                      </div>
                    )}
                  </div>

                  {/* Ad Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-secondary text-lg font-bold text-white truncate">
                          {ad.name}
                        </h4>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-2 py-0.5 text-[10px] font-bold uppercase ${
                            ad.ad_type === 'pre_roll' 
                              ? 'bg-orange-500/20 text-orange-400' 
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {ad.ad_type?.replace('_', '-')}
                          </span>
                          <span className={`px-2 py-0.5 text-[10px] font-bold uppercase ${
                            ad.is_active 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-zinc-500/20 text-zinc-400'
                          }`}>
                            {ad.is_active ? 'Active' : 'Paused'}
                          </span>
                          {ad.channel_ids?.length > 0 ? (
                            <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-purple-500/20 text-purple-400">
                              {ad.channel_ids.length} Channel{ad.channel_ids.length > 1 ? 's' : ''}
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-white/10 text-white">
                              All Channels
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Stats Row */}
                    <div className="flex items-center gap-6 text-xs text-zinc-400 mb-3">
                      <div className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        <span>{ad.view_count?.toLocaleString() || 0} views</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Check className="w-3 h-3 text-green-500" />
                        <span>{ad.complete_count?.toLocaleString() || 0} completed</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <SkipForward className="w-3 h-3 text-yellow-500" />
                        <span>{ad.skip_count?.toLocaleString() || 0} skipped</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <MousePointerClick className="w-3 h-3 text-blue-500" />
                        <span>{ad.click_count?.toLocaleString() || 0} clicks</span>
                      </div>
                    </div>

                    {/* Sponsor */}
                    {ad.sponsor_name && (
                      <p className="text-xs text-zinc-500 mb-2">
                        Sponsor: <span className="text-white">{ad.sponsor_name}</span>
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      onClick={() => toggleAdActive(ad.id)}
                      className={`${ad.is_active ? 'bg-zinc-800 text-yellow-500' : 'bg-green-600 text-white'} hover:bg-zinc-700 text-xs px-3 py-2 h-auto rounded-none`}
                    >
                      {ad.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </Button>
                    <Button
                      onClick={() => setEditingAd(ad)}
                      className="bg-zinc-800 text-white hover:bg-zinc-700 text-xs px-3 py-2 h-auto rounded-none"
                    >
                      <Settings className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => deleteAd(ad.id)}
                      className="bg-zinc-800 text-red-500 hover:bg-red-600 hover:text-white text-xs px-3 py-2 h-auto rounded-none"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingAd && (
        <VideoAdEditModal
          ad={editingAd}
          channels={channels}
          onSave={saveAdChanges}
          onClose={() => setEditingAd(null)}
        />
      )}
    </motion.div>
  );
};

// Edit Modal Component
const VideoAdEditModal = ({ ad, channels, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: ad.name || "",
    description: ad.description || "",
    ad_type: ad.ad_type || "pre_roll",
    ad_format: ad.ad_format || "video",
    duration_seconds: ad.duration_seconds || 15,
    is_skippable: ad.is_skippable !== false,
    skip_after_seconds: ad.skip_after_seconds || 5,
    channel_ids: ad.channel_ids || [],
    priority: ad.priority || 0,
    sponsor_name: ad.sponsor_name || "",
    click_url: ad.click_url || "",
    is_active: ad.is_active !== false
  });

  const handleSubmit = () => {
    onSave({ id: ad.id, ...formData });
  };

  const toggleChannel = (channelId) => {
    if (formData.channel_ids.includes(channelId)) {
      setFormData({ ...formData, channel_ids: formData.channel_ids.filter(id => id !== channelId) });
    } else {
      setFormData({ ...formData, channel_ids: [...formData.channel_ids, channelId] });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-white/10 sticky top-0 bg-[#0a0a0a]">
          <h3 className="font-secondary text-lg font-bold uppercase text-white">
            Edit Video Ad
          </h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4 space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <label className="text-xs text-zinc-400 uppercase tracking-widest block mb-2">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full bg-zinc-900 border border-white/10 px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none"
                placeholder="Ad name"
              />
            </div>
            
            <div>
              <label className="text-xs text-zinc-400 uppercase tracking-widest block mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full bg-zinc-900 border border-white/10 px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none h-20"
                placeholder="Optional description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-zinc-400 uppercase tracking-widest block mb-2">Ad Type</label>
                <select
                  value={formData.ad_type}
                  onChange={(e) => setFormData({ ...formData, ad_type: e.target.value })}
                  className="w-full bg-zinc-900 border border-white/10 px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none"
                >
                  <option value="pre_roll">Pre-Roll (Before Stream)</option>
                  <option value="mid_roll">Mid-Roll (During Stream)</option>
                  <option value="both">Both (Pre &amp; Mid Roll)</option>
                </select>
              </div>
              
              <div>
                <label className="text-xs text-zinc-400 uppercase tracking-widest block mb-2">Duration (seconds)</label>
                <input
                  type="number"
                  value={formData.duration_seconds}
                  onChange={(e) => setFormData({ ...formData, duration_seconds: parseInt(e.target.value) || 15 })}
                  className="w-full bg-zinc-900 border border-white/10 px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none"
                  min={5}
                  max={120}
                />
              </div>
            </div>
          </div>

          {/* Skip Settings */}
          <div className="border-t border-white/10 pt-4">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-white">Allow Skip</span>
              <button
                onClick={() => setFormData({ ...formData, is_skippable: !formData.is_skippable })}
                className={`w-12 h-6 rounded-full transition-colors ${formData.is_skippable ? 'bg-orange-500' : 'bg-zinc-700'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full transition-transform ${formData.is_skippable ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
            
            {formData.is_skippable && (
              <div>
                <label className="text-xs text-zinc-400 uppercase tracking-widest block mb-2">Skip After (seconds)</label>
                <input
                  type="number"
                  value={formData.skip_after_seconds}
                  onChange={(e) => setFormData({ ...formData, skip_after_seconds: parseInt(e.target.value) || 5 })}
                  className="w-full bg-zinc-900 border border-white/10 px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none"
                  min={0}
                  max={30}
                />
              </div>
            )}
          </div>

          {/* Channel Selection */}
          <div className="border-t border-white/10 pt-4">
            <label className="text-xs text-zinc-400 uppercase tracking-widest block mb-2">
              Target Channels <span className="text-zinc-600">(Leave empty for all channels)</span>
            </label>
            <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto">
              {channels.map(channel => (
                <button
                  key={channel.channel_id || channel.id}
                  onClick={() => toggleChannel(channel.channel_id || channel.id)}
                  className={`flex items-center gap-2 p-2 border transition-colors ${
                    formData.channel_ids.includes(channel.channel_id || channel.id)
                      ? 'border-orange-500 bg-orange-500/10'
                      : 'border-white/10 hover:border-white/20'
                  }`}
                >
                  {channel.logo_url ? (
                    <img src={channel.logo_url} alt="" className="w-6 h-6 object-contain" />
                  ) : (
                    <Radio className="w-4 h-4 text-zinc-500" />
                  )}
                  <span className="text-xs text-white truncate">{channel.abbreviation || channel.name}</span>
                </button>
              ))}
            </div>
            {formData.channel_ids.length === 0 && (
              <p className="text-xs text-green-400 mt-2">This ad will play on ALL channels</p>
            )}
          </div>

          {/* Sponsor Info */}
          <div className="border-t border-white/10 pt-4 space-y-4">
            <div>
              <label className="text-xs text-zinc-400 uppercase tracking-widest block mb-2">Sponsor Name</label>
              <input
                type="text"
                value={formData.sponsor_name}
                onChange={(e) => setFormData({ ...formData, sponsor_name: e.target.value })}
                className="w-full bg-zinc-900 border border-white/10 px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none"
                placeholder="Optional sponsor name"
              />
            </div>
            
            <div>
              <label className="text-xs text-zinc-400 uppercase tracking-widest block mb-2">Click URL</label>
              <input
                type="url"
                value={formData.click_url}
                onChange={(e) => setFormData({ ...formData, click_url: e.target.value })}
                className="w-full bg-zinc-900 border border-white/10 px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none"
                placeholder="https://sponsor-website.com"
              />
            </div>

            <div>
              <label className="text-xs text-zinc-400 uppercase tracking-widest block mb-2">Priority</label>
              <input
                type="number"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                className="w-full bg-zinc-900 border border-white/10 px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none"
                min={0}
                max={100}
              />
              <p className="text-xs text-zinc-600 mt-1">Higher priority ads are shown more often</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-white/10 sticky bottom-0 bg-[#0a0a0a]">
          <Button
            onClick={onClose}
            className="bg-zinc-800 text-white hover:bg-zinc-700 text-xs px-4 py-2 h-auto rounded-none"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="bg-orange-500 text-black hover:bg-orange-400 text-xs px-4 py-2 h-auto rounded-none"
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VideoAdsTab;
